import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';

import {labels} from '../../../../../../../util/utils';
//import getAvailableEntityList, {getAdminAvailableEntityList} from './getAvailableEntityList';
import getAvailableEntityList, {
	getAdminAvailableEntityList
} from '../../../../../../../util/contactsDB/getAvailableEntityList';
import {
	ACCESS_TO_CONTACT_LIST,
	ACCESS_TO_DASHBOARD,
	DASHBOARD_VIEWS,
	ENTITIES,
	HAS_EXTERNAL,
	ACCESS_TO_GROUP_RISK_ASSESMENT,
	ENTITY_ADMIN_ACCESS,
	PROJECT_OWNER_ACCESS,
	USER_LIST
} from '../utils/constants';
import {rootEntityId} from '../../../../../../../util/uiconstants';

import OutputText from '@ey/canvascoreservices/OutputText';
import Icon from '@ey/canvascoreservices/Icon';
import {IconoirInfoCircle} from '@ey-xd/motif-icon';

import selectAccessPermissions from '../../../../../../../reducers/Project/selectors/selectAccessPermissions';

import ToggleSwitch from '../../../../../../CoreWrappers/ToggleSwitch';
import Hr from '../../../../../../Common/Hr/HrAlt';
import CheckboxGroup from '../../../../../../Common/CheckboxGroup';
import Tooltip from '../../../../../../CoreWrappers/Tooltip';
import StyledDefineAccess from './StyledDefineAccess';
import SelectWithCheckbox from '../../../../../../CoreWrappers/SelectWithCheckbox';

const AccessDefinePanel = ({values, errors, handleChange, handleSubmit, handleReset, isLoading}) => {
	const userGui = useSelector((state) => state.userPreferences?.gui);
	const project = useSelector((state) => state.project);
	const userListPerm = project?.users;
	const isGroupRiskAssessmentProject = project?.isGroupRiskAssessmentProject;
	const userData = userListPerm?.find(({userId}) => userId === userGui);
	const isProjectOwner = !!userData?.isProjectOwner;
	const orgChart = useSelector((store) => store.dashboardContext?.orgChart);
	const {groupRiskAssessmentAccess} = selectAccessPermissions(project);
	const getFullEntities = () => getAvailableEntityList(orgChart);
	const getFilteredEntities = () => getAdminAvailableEntityList(orgChart, userGui);
	const entitiesList = isProjectOwner ? getFullEntities() : getFilteredEntities();

	const renderLabel = (label) => (
		<OutputText ellipsis title={label}>
			{label}
		</OutputText>
	);

	const renderTooltip = (label) => {
		const props = {
			placement: 'top',
			tooltipText: label
		};

		return <Icon src={IconoirInfoCircle} title={label} />;
		// return <Tooltip {...props} trigger={<Icon src={IconoirInfoCircle} />} />;
		// TODO: https://github.com/ey-org/Motif-React/issues/1608 open motif issue for tooltips inside dropdowns
	};

	const renderEntities = () => {
		const id = ENTITIES;

		const disabledOptionCondition = (id) => isGroupRiskAssessmentProject && id === rootEntityId;

		const options = entitiesList.map((ent) => ({
			...ent,
			disabled: disabledOptionCondition(ent.id)
		}));

		const props = {
			id,
			value: values[id],
			options,
			onChange: (value) => {
				let newValue = value;
				if (value.length === 0) newValue = null;
				handleChange(id, newValue);
			},
			label: labels.placeholderEntities,
			labels: {
				allSelected: labels.allEntitles,
				multiSelect: labels.someEntities,
				nonSelected: ''
			},
			disabled: isLoading || values[PROJECT_OWNER_ACCESS],
			isOptionDisabled: (option) => disabledOptionCondition(option.id)
		};

		return <SelectWithCheckbox {...props} />;
	};

	const renderGroupRiskAssessment = () => {
		const id = ACCESS_TO_GROUP_RISK_ASSESMENT;
		const tooltipProps = {
			placement: 'left',
			tooltipText: labels.externalUserDenyMessage
		};
		const props = {
			id,
			onLabel: labels.toggleContactYes,
			offLabel: labels.toggleContactNo,
			checked: values.hasExternal ? false : values[id]?? false,
			disabled: values.hasExternal || values[PROJECT_OWNER_ACCESS],
			onChange: () => {
				handleChange(id, !values[id]);
			}
		};

		return (
			<div className="toggle-content">
				{renderLabel(labels.groupRiskAssessment)}
				{values.hasExternal ? (
					<Tooltip {...tooltipProps} trigger={<ToggleSwitch {...props} />} />
				) : (
					<ToggleSwitch {...props} />
				)}
			</div>
		);
	};

	const renderDashboardViews = () => {
		const id = DASHBOARD_VIEWS;

		const props = {
			value: values[id],
			onChange: (value) => handleChange(id, value),
			groupDisabled: isLoading || values[PROJECT_OWNER_ACCESS]
		};

		return (
			<div className="toggle-content">
				{renderLabel(labels.dashBoardsViews)}
				<CheckboxGroup {...props} />
			</div>
		);
	};

	const renderAccessToContactList = () => {
		const id = ACCESS_TO_CONTACT_LIST;

		const props = {
			id,
			onLabel: labels.toggleContactYes,
			offLabel: labels.toggleContactNo,
			checked: values[id]?? false,
			disabled: isLoading || values[PROJECT_OWNER_ACCESS] || values[ENTITY_ADMIN_ACCESS],
			onChange: () => {
				handleChange(id, !values[id]);
			}
		};

		return (
			<div className="toggle-content">
				{renderLabel(labels.accessToContactListLabel)}
				<ToggleSwitch {...props} />
			</div>
		);
	};

	const renderProjectOwnerToggle = () => {
		const id = PROJECT_OWNER_ACCESS;

		const getEntitiesForPO = (checked) => {
			if (checked) return [{...entitiesList.filter((item) => item.id === rootEntityId)[0]}];
			return null;
		};

		const props = {
			id,
			onLabel: labels.toggleContactYes,
			offLabel: labels.toggleContactNo,
			checked: values.hasExternal ? false : values[PROJECT_OWNER_ACCESS],
			disabled: values.hasExternal || values[ENTITY_ADMIN_ACCESS],
			onChange: (e) => {
				let object = {
					[ACCESS_TO_CONTACT_LIST]: e.target?.checked,
					[ENTITIES]: getEntitiesForPO(e.target?.checked),
					[DASHBOARD_VIEWS]: values[DASHBOARD_VIEWS].map((item) => ({...item, checked: e.target?.checked}))
				};
				if (isGroupRiskAssessmentProject) {
					object = {...object, [ACCESS_TO_GROUP_RISK_ASSESMENT]: e.target?.checked};
				}
				handleChange(PROJECT_OWNER_ACCESS, e.target?.checked, object);
			}
		};

		return (
			<div className="toggle-content">
				<div className="header">
					{renderLabel(labels.projectOwnerAccessLabel)}
					{renderTooltip(labels.projectOwnerAccessToogleInfo)}
				</div>
				<ToggleSwitch {...props} />
			</div>
		);
	};

	const renderEntityAdminToggle = () => {
		const id = ENTITY_ADMIN_ACCESS;

		const props = {
			id,
			onLabel: labels.toggleContactYes,
			offLabel: labels.toggleContactNo,
			checked: values[ENTITY_ADMIN_ACCESS],
			disabled: values[PROJECT_OWNER_ACCESS],
			onChange: (e) => {
				handleChange(ENTITY_ADMIN_ACCESS, e.target?.checked, {[ACCESS_TO_CONTACT_LIST]: e.target?.checked});
			}
		};

		return (
			<div className="toggle-content">
				<div className="header">
					{renderLabel(labels.entityAdminAccessLabel)}
					{renderTooltip(labels.entityAdminAccessToogleInfo)}
				</div>
				<ToggleSwitch {...props} />
			</div>
		);
	};

	return (
		<StyledDefineAccess className="manage-access">
			{isProjectOwner && renderProjectOwnerToggle()}
			{isProjectOwner && renderEntityAdminToggle()}
			{renderEntities()}
			<Hr />
			{renderDashboardViews()}
			{groupRiskAssessmentAccess && renderGroupRiskAssessment()}
			{renderAccessToContactList()}
		</StyledDefineAccess>
	);
};

//TODO: complete prop types.
AccessDefinePanel.propTypes = {};

export default AccessDefinePanel;
